// inv/src/components/investor-page/modules/pCoInvestNetwork.jsx
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../../config';
import axios from 'axios';
import { Link } from 'react-router-dom';
import CountryFlag from '../../countryFlags/countryFlag';
import InvestorLogo from '../../data/InvestorLogo';
import LoadingSpinner from '../../ui/LoadingSpinner';
import { Badge } from '../../ui/badge';
import { ExternalLink, TrendingUp, Users, Calendar, DollarSign } from 'lucide-react';
import HoverHint from '../../ui/hoverHint';
import { getAmountRangeByCode } from '../../../lib/amountBins';
import { buildInvestorProfilePath, getLanguagePrefix } from '../../investor-display/helpers';

const summaryGridClass = "grid grid-cols-[repeat(auto-fit,minmax(min(100%,11rem),1fr))] gap-3 mb-5";
const summaryCardClass = "min-w-0 bg-gray-50 p-3 rounded-lg text-center";
const summaryLabelClass = "flex flex-wrap items-center justify-center gap-1.5 mb-1 text-center";
const summaryTextClass = "min-w-0 text-xs font-medium leading-snug text-gray-700 break-words";
const metricGridClass = "grid grid-cols-[repeat(auto-fit,minmax(min(100%,7.5rem),1fr))] gap-3 mb-4";
const metricCardClass = "min-w-0 text-center bg-gray-50 p-3 rounded-md";
const metricLabelClass = "flex flex-wrap items-center justify-center gap-1.5 text-sm leading-snug text-gray-500 break-words";
const hintClass = "w-4 h-4 shrink-0 bg-gray-400 text-white rounded-full flex items-center justify-center cursor-help";

const CoInvestNetwork = ({ investorId }) => {
    const { t, i18n } = useTranslation();
    const [networkData, setNetworkData] = useState(null);
    const [partnerNames, setPartnerNames] = useState({});
    const [dealInfo, setDealInfo] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const stageLabels = {
        0: t('pageInvProf.stageUnknown'),
        1: t('pageInvProf.stagePreSeed'),
        2: t('pageInvProf.stageSeed'),
        3: t('pageInvProf.stageSeriesA'),
        4: t('pageInvProf.stageSeriesB'),
        5: t('pageInvProf.stageSeriesC'),
        6: t('pageInvProf.stageGrowth'),
        7: t('pageInvProf.stagePE'),
        8: t('pageInvProf.stageIPO')
    };


    useEffect(() => {
        const fetchNetworkData = async () => {
            if (!investorId) return;

            try {
                setLoading(true);
                setError(null);

                const response = await axios.get(`${API_URL}/investors-limited/${investorId}/coinvest-public`, {
                    params: { by: 'id' },
                });

                const data = response.data;
                setNetworkData(data);

                if (data.edges && data.edges.length > 0) {
                    const partnerDetails = {};
                    const dealDetails = {};

                    const partnerPromises = data.edges.map(async (edge) => {
                        try {
                            const partnerResponse = await axios.get(`${API_URL}/investors-limited/${edge.pid}`, {
                                params: { by: '_id' }
                            });
                            if (partnerResponse.data) {
                                partnerDetails[edge.pid] = {
                                    id: partnerResponse.data.id,
                                    name: partnerResponse.data.name
                                };
                            }
                        } catch (err) {
                            console.warn(`Failed to fetch partner details for ${edge.pid}:`, err);
                        }
                    });

                    await Promise.all(partnerPromises);
                    setPartnerNames(partnerDetails);

                    const allDealIds = new Set();

                    data.edges.forEach(edge => {
                        if (edge.recent_ids && Array.isArray(edge.recent_ids)) {
                            edge.recent_ids.forEach(deal => {
                                if (deal.id) allDealIds.add(deal.id);
                            });
                        }
                    });

                    const dealIdsArray = Array.from(allDealIds).slice(0, 20);
                    console.log(`[pCoInvestNetwork] Loading ${dealIdsArray.length} deals out of ${allDealIds.size} total`);
                    for (let i = 0; i < dealIdsArray.length; i++) {
                        const dealId = dealIdsArray[i];
                        const dealData = await fetchDealInfo(dealId);
                        if (dealData) {
                            dealDetails[dealId] = dealData;
                        }
                        if (i < dealIdsArray.length - 1) {
                            await new Promise(resolve => setTimeout(resolve, 100));
                        }
                    }
                    setDealInfo(dealDetails);
                }
            } catch (err) {
                console.error('Error fetching co-investment network:', err);
                setError('Failed to load co-investment network');
            } finally {
                setLoading(false);
            }
        };

        fetchNetworkData();
    }, [investorId]);

    const generatePartnerLink = (partnerId) => {
        const partnerData = partnerNames[partnerId];
        if (!partnerData || !partnerData.id || !partnerData.name) {
            const prefix = getLanguagePrefix(i18n.language);
            return `${prefix}/investor-profile/${partnerId}`;
        }

        return buildInvestorProfilePath({
            investor: partnerData,
            language: i18n.language,
        });
    };

    const formatRecency = (recency) => {
        return Math.round((recency / 10) * 100);
    };

    const formatWeight = (weightBps) => {
        return (weightBps / 100).toFixed(1);
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatAmount = (amount, currency = '') => {
        if (!amount || amount === 0) return `${currency}0`;

        const num = Number(amount);
        if (num >= 1e9) {
            return `${currency}${(num / 1e9).toFixed(1)}b`;
        } else if (num >= 1e6) {
            return `${currency}${(num / 1e6).toFixed(1)}m`;
        } else if (num >= 1e3) {
            return `${currency}${(num / 1e3).toFixed(1)}k`;
        } else {
            return `${currency}${num.toLocaleString()}`;
        }
    };

    const fetchDealInfo = async (dealId) => {
        try {
            const response = await axios.get(`${API_URL}/deals/${dealId}/basic`);
            return response.data;
        } catch (err) {
            console.warn(`Failed to fetch deal info for ${dealId}:`, err);
            return null;
        }
    };

    if (loading) {
        return (
            <div className="w-full rounded-md bg-white p-6 shadow">
                <div className="flex items-center justify-center py-8">
                    <LoadingSpinner />
                </div>
            </div>
        );
    }

    if (error || !networkData) {
        return null;
    }

    if (networkData.visible_count === 0 && networkData.hidden_count === 0) {
        return null;
    }

    return (
        <div className="w-full rounded-md bg-white p-6 shadow">
            <div className="mb-6">
                <h3 className="flex flex-wrap items-center gap-2 text-2xl text-stone-900 font-semibold leading-tight break-words">
                    <Users className="h-5 w-5 shrink-0" />
                    <span className="min-w-0">{t('pageInvProf.coInvestNetwork')}</span>
                </h3>

            </div>

            <div className={summaryGridClass}>
                <div className={summaryCardClass}>
                    <div className={summaryLabelClass}>
                        <Users className="h-4 w-4 shrink-0 text-blue-600" />
                        <span className={summaryTextClass}>{t('pageInvProf.totalPartners')}</span>
                        <HoverHint message={t('pageInvProf.totalPartnersHint')}>
                            <div className={hintClass}>
                                <span className="text-xs font-bold">?</span>
                            </div>
                        </HoverHint>
                    </div>
                    <div className="text-lg font-bold text-gray-900">
                        {networkData.universe?.partners_total || 0}
                    </div>
                </div>

                <div className={summaryCardClass}>
                    <div className={summaryLabelClass}>
                        <TrendingUp className="h-4 w-4 shrink-0 text-purple-600" />
                        <span className={summaryTextClass}>{t('pageInvProf.keyPartners')}</span>
                        <HoverHint message={t('pageInvProf.keyPartnersHint')}>
                            <div className={hintClass}>
                                <span className="text-xs font-bold">?</span>
                            </div>
                        </HoverHint>
                    </div>
                    <div className="text-lg font-bold text-gray-900">
                        {networkData.universe?.partners_ge2 || 0}
                    </div>
                </div>

                <div className={summaryCardClass}>
                    <div className={summaryLabelClass}>
                        <Users className="h-4 w-4 shrink-0 text-indigo-600" />
                        <span className={summaryTextClass}>{t('pageInvProf.partnersShown')}</span>
                        <HoverHint message={t('pageInvProf.partnersShownHint')}>
                            <div className={hintClass}>
                                <span className="text-xs font-bold">?</span>
                            </div>
                        </HoverHint>
                    </div>
                    <div className="text-lg font-bold text-gray-900">
                        {networkData.edges?.length || 0} / {networkData.total_core_partners || 0}
                    </div>
                </div>


                <div className={summaryCardClass}>
                    <div className={summaryLabelClass}>
                        <Calendar className="h-4 w-4 shrink-0 text-orange-600" />
                        <span className={summaryTextClass}>{t('pageInvProf.lastUpdated')}</span>
                        <HoverHint message={t('pageInvProf.lastUpdatedHint')}>
                            <div className={hintClass}>
                                <span className="text-xs font-bold">?</span>
                            </div>
                        </HoverHint>
                    </div>
                    <div className="text-xs leading-snug text-gray-600 break-words">
                        {networkData.updated_at ? formatDate(networkData.updated_at) : t('pageInvProf.unknown')}
                    </div>
                </div>

            </div>

            {networkData.edges && networkData.edges.length > 0 && (
                <div>
                    <h4 className="text-lg font-medium text-gray-900  mb-4">
                        {networkData.edges.length === 1 ? t('pageInvProf.topPartner') : t('pageInvProf.topPartners')}
                    </h4>

                    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(18rem,0.9fr)] gap-6 items-start">
                        <div className="min-w-0 space-y-4">
                            {networkData.edges.map((partner, index) => (
                                <div key={partner.pid} className="min-w-0 border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                    <div className="flex min-w-0 items-start gap-3 mb-4">
                                        <div className="flex shrink-0 items-center">
                                            <InvestorLogo
                                                id={partner.pid}
                                                className="w-6 h-6"
                                                alt="Partner logo"
                                            />
                                        </div>
                                        <Link
                                            to={generatePartnerLink(partner.pid)}
                                            className="min-w-0 inline-flex flex-wrap items-center gap-1 text-lg font-bold text-gray-900 hover:underline whitespace-normal break-words leading-tight"
                                        >
                                            <span className="min-w-0 break-words">{partnerNames[partner.pid]?.name || `Partner ${String(partner.pid).slice(-8)}`}</span>
                                            <ExternalLink size={14} className="shrink-0" />
                                        </Link>
                                    </div>

                                    <div className={metricGridClass}>
                                        <div className={metricCardClass}>
                                            <div className={metricLabelClass}>
                                                <span className="min-w-0">{t('pageInvProf.jointDeals')}</span>
                                                <HoverHint message={t('pageInvProf.jointDealsHint')}>
                                                    <div className={hintClass}>
                                                        <span className="text-xs font-bold">?</span>
                                                    </div>
                                                </HoverHint>
                                            </div>
                                            <div className="text-xl font-semibold text-gray-900">{partner.deals}</div>
                                        </div>

                                        <div className={metricCardClass}>
                                            <div className={metricLabelClass}>
                                                <span className="min-w-0">{t('pageInvProf.leadPercentage')}</span>
                                                <HoverHint message={t('pageInvProf.leadPercentageHint')}>
                                                    <div className={hintClass}>
                                                        <span className="text-xs font-bold">?</span>
                                                    </div>
                                                </HoverHint>
                                            </div>
                                            <div className="text-xl font-semibold text-gray-900">{partner.lead_pct}%</div>
                                        </div>

                                        <div className={metricCardClass}>
                                            <div className={metricLabelClass}>
                                                <span className="min-w-0">{t('pageInvProf.recency')}</span>
                                                <HoverHint message={t('pageInvProf.recencyHint')}>
                                                    <div className={hintClass}>
                                                        <span className="text-xs font-bold">?</span>
                                                    </div>
                                                </HoverHint>
                                            </div>
                                            <div className="text-xl font-semibold text-gray-900">{formatRecency(partner.recency)}%</div>
                                        </div>

                                        <div className={metricCardClass}>
                                            <div className={metricLabelClass}>
                                                <span className="min-w-0">{t('pageInvProf.weight')}</span>
                                                <HoverHint message={t('pageInvProf.weightHint')}>
                                                    <div className={hintClass}>
                                                        <span className="text-xs font-bold">?</span>
                                                    </div>
                                                </HoverHint>
                                            </div>
                                            <div className="text-xl font-semibold text-gray-900">{formatWeight(partner.weight_bps)}%</div>
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        {partner.years && partner.years.length > 0 && (
                                            <div>
                                                <div className="text-sm font-medium text-gray-700 mb-2">{t('pageInvProf.recentYears')}</div>
                                                <div className="flex flex-wrap gap-2">
                                                    {partner.years.map((year, idx) => (
                                                        <div key={idx} className="flex items-center gap-2">
                                                            <div className="flex items-center gap-1">
                                                                <Badge text={`${year.y}: ***`} variant="v1" />
                                                                <HoverHint message={t('pageInvProf.startupProfessionalOnlyHint')}>
                                                                    <div className="w-4 h-4 bg-gray-400 text-white rounded-full flex items-center justify-center cursor-help">
                                                                        <span className="text-xs font-bold">?</span>
                                                                    </div>
                                                                </HoverHint>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {partner.stages && partner.stages.length > 0 && (
                                            <div>
                                                <div className="text-sm font-medium text-gray-700 mb-2">
                                                    {t('pageInvProf.stages')}
                                                </div>
                                                <div className="flex flex-wrap gap-2">
                                                    {partner.stages.map((stage, idx) => {
                                                        const label = stage.label || stageLabels[stage.code] || String(stage.code);
                                                        return (
                                                            <div key={idx} className="flex items-center gap-2">
                                                                <div className="flex items-center gap-1">
                                                                    <Badge text={`${label}: ***`} variant="v2" />
                                                                    <HoverHint message={t('pageInvProf.startupProfessionalOnlyHint')}>
                                                                        <div className="w-4 h-4 bg-gray-400 text-white rounded-full flex items-center justify-center cursor-help">
                                                                            <span className="text-xs font-bold">?</span>
                                                                        </div>
                                                                    </HoverHint>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {partner.amounts && partner.amounts.length > 0 && (
                                            <div>
                                                <div className="text-sm font-medium text-gray-700 mb-2">{t('pageInvProf.amounts')}</div>
                                                <div className="flex flex-wrap gap-2">
                                                    {partner.amounts.map((amount, idx) => (
                                                        <div key={idx} className="flex items-center gap-2">
                                                            <div className="flex items-center gap-1">
                                                                <Badge
                                                                    text={`${getAmountRangeByCode(amount.code)}: ***`}
                                                                    variant="v3"
                                                                />
                                                                <HoverHint message={t('pageInvProf.startupProfessionalOnlyHint')}>
                                                                    <div className="w-4 h-4 bg-gray-400 text-white rounded-full flex items-center justify-center cursor-help">
                                                                        <span className="text-xs font-bold">?</span>
                                                                    </div>
                                                                </HoverHint>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {partner.recent_ids && partner.recent_ids.length > 0 && (
                                            <div className="p-3 rounded">
                                                <div className="font-medium text-gray-700 mb-2">{t('pageInvProf.recentDealsWithPartner')}</div>
                                                <div className="text-sm text-gray-600">
                                                    <div className="min-w-0">
                                                        <div className="flex min-w-0 flex-wrap items-center gap-2">
                                                            <span>{t('pageInvProf.recentDealsCount', { count: partner.recent_ids.length })}</span>
                                                            <HoverHint message={t('pageInvProf.startupProfessionalOnlyHint')}>
                                                                <div className={hintClass}>
                                                                    <span className="text-xs font-bold">?</span>
                                                                </div>
                                                            </HoverHint>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="min-w-0 space-y-4">
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-700 leading-relaxed">
                                    {t('pageInvProf.coInvestDescription')}
                                </p>
                            </div>

                            <div className="min-w-0 border border-gray-200 rounded-lg overflow-hidden grid grid-cols-1 md:grid-cols-[minmax(0,1.15fr)_minmax(14rem,0.85fr)]">
                                <div className="min-h-48 p-4">
                                    <div
                                        className="w-full min-h-48 h-full rounded-lg bg-cover bg-center"
                                        style={{
                                            backgroundImage:
                                                "url('https://crino-cdn.nyc3.cdn.digitaloceanspaces.com/CTA_images/investor_profile_co_network.webp')",
                                        }}
                                    />
                                </div>

                                <div className="min-w-0 flex items-center justify-center p-4">
                                    <div className="min-w-0 text-center">
                                        <h5 className="text-lg font-semibold leading-tight text-gray-900 mb-2 break-words">
                                            {t('pageInvProf.discoverMorePartners')}
                                        </h5>
                                        <p className="text-sm leading-relaxed text-gray-600 mb-4 break-words">
                                            {t('pageInvProf.discoverMorePartnersDescription')}
                                        </p>
                                        <Link
                                            to={`${i18n.language === 'en' ? '' : `/${i18n.language}`
                                                }/pricing`}
                                            className="inline-flex min-h-8 items-center justify-center rounded-md px-3 py-2 text-center text-xs font-medium leading-tight transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 bg-stone-900 text-white shadow hover:bg-stone-900/90 cursor-pointer break-words"
                                        >
                                            {t('pageInvProf.explorePremium')}
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            )}

        </div>
    );
};

export default CoInvestNetwork;
